import {
  NOTIFICATION_TARGET_TYPES,
  getNotificationEventDefinition,
  getNotificationRouteTarget,
} from "./notificationRegistryAdapter.js";
import {
  getBoardItemTypeForCollectionName,
} from "../../shared/board/boardRegistry.js";

export const NOTIFICATION_CLICK_OUTCOMES = Object.freeze({
  DETAIL_ROUTE: NOTIFICATION_TARGET_TYPES.DETAIL_ROUTE,
  BOARD_ROUTE: NOTIFICATION_TARGET_TYPES.BOARD_ROUTE,
  MODAL_SURFACE: NOTIFICATION_TARGET_TYPES.MODAL_SURFACE,
  INFORMATIONAL_ONLY: NOTIFICATION_TARGET_TYPES.INFORMATIONAL_ONLY,
});

function getNotificationTargetDefinition(triggerEvent) {
  const definition = getNotificationEventDefinition(triggerEvent);
  return definition?.target || {
    type: NOTIFICATION_CLICK_OUTCOMES.DETAIL_ROUTE,
    key: triggerEvent,
  };
}

/**
 * Returns whether the event belongs to the persistent approval notification set.
 * @param {string} triggerEvent Notification trigger event id.
 * @returns {boolean} True when the event is an approval event.
 */
export function isApprovalNotification(triggerEvent) {
  const target = getNotificationTargetDefinition(triggerEvent);
  return target.type === NOTIFICATION_CLICK_OUTCOMES.MODAL_SURFACE &&
    target.key === "approval";
}

/**
 * Resolves the click outcome for an in-app notification.
 * @param {object} notification Notification document data.
 * @param {object} [currentUserProfile] Current user profile.
 * @returns {object} Notification click outcome.
 */
export function resolveNotificationClickTarget(
    notification,
    currentUserProfile = {},
) {
  if (
    notification.parentType === "recalls" &&
    notification.contextType === "note" &&
    notification.parentId
  ) {
    return {
      type: NOTIFICATION_CLICK_OUTCOMES.BOARD_ROUTE,
      view: "recalls",
      recallId: notification.parentId,
      noteId: notification.contextId || null,
    };
  }

  if (
    notification.parentType === "ordering" &&
    notification.contextType === "note" &&
    notification.parentId
  ) {
    return {
      type: NOTIFICATION_CLICK_OUTCOMES.BOARD_ROUTE,
      view: "ordering",
      itemId: notification.parentId,
      noteId: notification.contextId || null,
    };
  }

  const target = getNotificationTargetDefinition(notification.triggerEvent);

  if (target.type === NOTIFICATION_CLICK_OUTCOMES.MODAL_SURFACE) {
    return resolveModalTarget(notification, target);
  }

  if (target.type === NOTIFICATION_CLICK_OUTCOMES.BOARD_ROUTE) {
    return resolveBoardTarget(notification, target, currentUserProfile);
  }

  if (target.type === NOTIFICATION_CLICK_OUTCOMES.INFORMATIONAL_ONLY) {
    return {
      type: NOTIFICATION_CLICK_OUTCOMES.INFORMATIONAL_ONLY,
      reason: target.key || "informational_only",
    };
  }

  const detailTarget = getDetailRouteTarget(notification);
  if (detailTarget) {return detailTarget;}

  return {
    type: NOTIFICATION_CLICK_OUTCOMES.INFORMATIONAL_ONLY,
    reason: "unresolved_target",
  };
}

function resolveModalTarget(notification, target) {
  if (target.key === "approval" && notification.parentId) {
    return {
      type: NOTIFICATION_CLICK_OUTCOMES.MODAL_SURFACE,
      surface: "approval",
      approvalId: notification.parentId,
      approvalDomain: notification.approvalDomain || null,
    };
  }

  return {
    type: NOTIFICATION_CLICK_OUTCOMES.INFORMATIONAL_ONLY,
    reason: "unresolved_modal_target",
  };
}

function hasDrugPermission(currentUserProfile, ...permissionKeys) {
  if (currentUserProfile.role === "admin" ||
    currentUserProfile.isDrugAdmin) {
    return true;
  }

  const permissions = currentUserProfile.drugPermissions || {};
  return permissionKeys.some((key) => permissions[key] === true);
}

function resolveDrugFallbackSubView(currentUserProfile) {
  if (hasDrugPermission(
      currentUserProfile,
      "logUse",
      "restock",
      "relocate",
      "correction",
      "logLoss",
      "startAudit",
  )) {
    return "actions";
  }

  if (hasDrugPermission(currentUserProfile, "viewDashboard")) {
    return "stock";
  }

  return null;
}

function resolveBoardTarget(notification, target, currentUserProfile) {
  if (target.key === "drug_stock") {
    return {
      type: NOTIFICATION_CLICK_OUTCOMES.BOARD_ROUTE,
      view: "drugs",
      subView: "stock",
    };
  }

  if (target.key === "drug_audit") {
    const hasAuditPermission = hasDrugPermission(
        currentUserProfile,
        "viewAudits",
    );

    return {
      type: NOTIFICATION_CLICK_OUTCOMES.BOARD_ROUTE,
      view: "drugs",
      subView: hasAuditPermission
        ? "audit"
        : resolveDrugFallbackSubView(currentUserProfile),
    };
  }

  if (target.key === "drug_history") {
    const canSeeHistory = hasDrugPermission(
        currentUserProfile,
        "viewHistory",
    );

    return {
      type: NOTIFICATION_CLICK_OUTCOMES.BOARD_ROUTE,
      view: "drugs",
      subView: canSeeHistory
        ? "history"
        : resolveDrugFallbackSubView(currentUserProfile),
    };
  }

  if (notification.contextType === "drug" ||
    notification.parentType === "drugs") {
    return {
      type: NOTIFICATION_CLICK_OUTCOMES.BOARD_ROUTE,
      view: "drugs",
    };
  }

  const routeTarget = getNotificationRouteTarget(target.key);
  if (routeTarget?.view) {
    const complianceTarget =
      routeTarget.contextType === "compliance_requirement";
    return {
      type: NOTIFICATION_CLICK_OUTCOMES.BOARD_ROUTE,
      view: routeTarget.view,
      subView: routeTarget.subView || null,
      surface: routeTarget.surface || null,
      contextType: routeTarget.contextType || null,
      contextId: routeTarget.contextType === "ordering_item" ||
          complianceTarget ?
        (notification.contextId || notification.parentId || null) :
        null,
      itemId: complianceTarget ? notification.parentId || null : null,
      requirementId: complianceTarget ?
        notification.contextId || null :
        null,
      notificationId: complianceTarget ? notification.id || null : null,
    };
  }

  return {
    type: NOTIFICATION_CLICK_OUTCOMES.INFORMATIONAL_ONLY,
    reason: "unresolved_board_target",
  };
}

function resolveParentItemType(parentType) {
  if (!parentType) {return "item";}

  return getBoardItemTypeForCollectionName(parentType) ||
    (parentType.endsWith("s") ? parentType.slice(0, -1) : parentType);
}

function getDetailRouteTarget(notification) {
  let itemId;
  let itemType;
  let view;

  if (notification.contextType === "task") {
    itemId = notification.contextId;
    itemType = "task";
    view = notification.parentType || "tasks";
  } else {
    itemId = notification.parentId;
    itemType = resolveParentItemType(notification.parentType);
    view = notification.parentType;
  }

  if (!itemId || !itemType || !view) {return null;}

  return {
    type: NOTIFICATION_CLICK_OUTCOMES.DETAIL_ROUTE,
    itemId,
    itemType,
    view,
  };
}
